import { useState, useRef, useEffect } from "react";
import { ArrowUp, ArrowDown, Filter, X } from "lucide-react";

/**
 * Excel-like header filter dropdown.
 *
 * Props:
 *  - label            (string)  header label displayed
 *  - values           (string[]) distinct values available for filtering
 *  - selected         (Set)     currently selected values (empty = no filter)
 *  - onSelectedChange (Set)     called with new Set
 *  - sortDir          ('asc' | 'desc' | null)
 *  - onSortChange     (dir | null)
 *  - renderValue      (value → JSX) optional custom renderer for value labels
 *  - testId           optional data-testid prefix
 *
 * Renders as a clickable header. When the popover is open, shows sort buttons +
 * a checkbox list of values. Indicators (filter active + sort direction) live
 * in the header itself so the layout stays compact.
 */
export default function ExcelHeaderFilter({
  label,
  values = [],
  selected,
  onSelectedChange,
  sortDir = null,
  onSortChange,
  renderValue,
  testId = "col",
  align = "left",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const popRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const active = selected && selected.size > 0;
  const filtered = q ? values.filter((v) => String(renderValue ? "" : v || "").toLowerCase().includes(q.toLowerCase()) || String(v || "").toLowerCase().includes(q.toLowerCase())) : values;

  const toggle = (v) => {
    const s = new Set(selected || []);
    if (s.has(v)) s.delete(v); else s.add(v);
    onSelectedChange(s);
  };
  const selectAll = () => onSelectedChange(new Set(values));
  const clear = () => onSelectedChange(new Set());

  return (
    <span className="relative inline-flex items-center gap-1">
      <button
        ref={btnRef}
        type="button"
        data-testid={`hdr-${testId}`}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 text-left uppercase text-xs tracking-wider px-1 -mx-1 py-0.5 rounded hover:bg-zinc-100 ${active ? "text-black font-semibold" : "text-zinc-500"}`}
      >
        <span>{label}</span>
        {sortDir === "asc" && <ArrowUp className="w-3 h-3" data-testid={`hdr-${testId}-sort-asc`} />}
        {sortDir === "desc" && <ArrowDown className="w-3 h-3" data-testid={`hdr-${testId}-sort-desc`} />}
        {active && <span className="inline-flex items-center gap-0.5 text-[10px] font-mono bg-black text-white rounded px-1" data-testid={`hdr-${testId}-active`}>
          <Filter className="w-2.5 h-2.5" />{selected.size}
        </span>}
      </button>
      {open && (
        <div
          ref={popRef}
          className={`absolute top-full mt-1 ${align === "right" ? "right-0" : "left-0"} z-30 min-w-[220px] w-max max-w-[300px] bg-white border border-zinc-200 rounded-md shadow-lg text-sm`}
          data-testid={`hdr-${testId}-menu`}
        >
          <div className="p-1 border-b border-zinc-200 grid grid-cols-2 gap-1">
            <button
              type="button"
              data-testid={`hdr-${testId}-asc`}
              onClick={() => { onSortChange(sortDir === "asc" ? null : "asc"); }}
              className={`flex items-center justify-center gap-1 text-xs h-8 rounded hover:bg-zinc-100 ${sortDir === "asc" ? "bg-zinc-100 font-semibold" : ""}`}
            >
              <ArrowUp className="w-3 h-3" /> A → Z
            </button>
            <button
              type="button"
              data-testid={`hdr-${testId}-desc`}
              onClick={() => { onSortChange(sortDir === "desc" ? null : "desc"); }}
              className={`flex items-center justify-center gap-1 text-xs h-8 rounded hover:bg-zinc-100 ${sortDir === "desc" ? "bg-zinc-100 font-semibold" : ""}`}
            >
              <ArrowDown className="w-3 h-3" /> Z → A
            </button>
          </div>
          {values.length > 0 && (
            <div className="p-1 border-b border-zinc-200 flex items-center gap-1">
              <button type="button" data-testid={`hdr-${testId}-selectall`} onClick={selectAll} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-black">Seleziona tutto</button>
              <span className="text-zinc-300">·</span>
              <button type="button" data-testid={`hdr-${testId}-clear`} onClick={clear} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-black">Cancella filtro</button>
              {values.length > 6 && (
                <input
                  placeholder="Cerca…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="ml-auto h-7 w-24 border rounded px-2 text-xs"
                />
              )}
            </div>
          )}
          {values.length > 0 ? (
            <div className="max-h-56 overflow-y-auto py-1">
              {filtered.map((v) => (
                <label
                  key={v == null ? "__unset" : v}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer text-sm"
                  data-testid={`hdr-${testId}-value-${v == null ? "unset" : v}`}
                >
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5"
                    checked={selected?.has(v) || false}
                    onChange={() => toggle(v)}
                  />
                  {renderValue ? renderValue(v) : <span className="truncate">{v == null ? <em className="text-zinc-400">— Non impostato</em> : String(v)}</span>}
                </label>
              ))}
              {filtered.length === 0 && <div className="text-xs text-zinc-400 px-3 py-4 text-center">Nessun valore</div>}
            </div>
          ) : (
            <div className="text-xs text-zinc-400 px-3 py-3 text-center">Solo ordinamento</div>
          )}
          <div className="border-t border-zinc-200 p-1 flex justify-end">
            <button type="button" onClick={() => setOpen(false)} className="text-xs text-zinc-500 hover:text-black inline-flex items-center gap-1 px-2 py-1"><X className="w-3 h-3" />Chiudi</button>
          </div>
        </div>
      )}
    </span>
  );
}
